/**
 * API service for School Growth Analytics endpoints (B-2026-0076).
 *
 * All endpoints are owner/channel_admin only on the backend.
 */

import { apiClient } from './api';
import type {
  ChannelAnalyticsOverview,
  ChannelAthleteActivity,
  ChannelMemberGrowth,
} from '../types/channelAnalytics';

/**
 * GET /api/channels/{slug}/analytics/overview
 *
 * Returns aggregate growth metrics for the channel.
 * Credit-ledger fields (token_burn_rate_30d, estimated_days_remaining) are
 * null when the credit economy is not activated — handle gracefully in UI.
 */
export async function getChannelAnalyticsOverview(
  slug: string,
): Promise<ChannelAnalyticsOverview> {
  const { data } = await apiClient.get<ChannelAnalyticsOverview>(
    `/channels/${slug}/analytics/overview`,
  );
  return data;
}

/**
 * GET /api/channels/{slug}/analytics/member-growth?weeks=N
 *
 * Returns weekly membership growth over the trailing N weeks (default 12).
 * Each bucket includes new_joins, departures (always 0), and cumulative_total.
 */
export async function getChannelMemberGrowth(
  slug: string,
  weeks = 12,
): Promise<ChannelMemberGrowth> {
  const { data } = await apiClient.get<ChannelMemberGrowth>(
    `/channels/${slug}/analytics/member-growth`,
    { params: { weeks } },
  );
  return data;
}

/**
 * GET /api/channels/{slug}/analytics/athlete-activity?days=N
 *
 * Returns per-athlete engagement summary for the trailing N days (default 30).
 * Results are ordered by last_eval_at DESC (most recently active first).
 */
export async function getChannelAthleteActivity(
  slug: string,
  days = 30,
): Promise<ChannelAthleteActivity> {
  const { data } = await apiClient.get<ChannelAthleteActivity>(
    `/channels/${slug}/analytics/athlete-activity`,
    { params: { days } },
  );
  return data;
}
